import { useState } from 'react'

import type { Entity } from '@models/Entity'

import { useContactpersoonAccounts } from '../../entity/contactpersonen/hooks/useContactpersoonAccounts'
import { MIN_QUERY_SIZE } from '../../entity/contactpersonen/services/fetchContactpersoonAccounts'
import type { AccountOption } from '../../entity/contactpersonen/types/AccountOption'
import type { Styles } from '../../theme/types/Styles'

import { SelectObject } from './SelectObject'

export { SelectAccount, SelectAccountOptions }

interface OptionsProps {
  accounts: AccountOption[]
  text: string
  onSearch: (text: string) => void
  loading?: boolean
  value: AccountOption | null
  onChange: (account: AccountOption | null) => void
  label?: string
  error?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  sx?: Styles
}

interface Props extends Omit<
  OptionsProps,
  'accounts' | 'text' | 'onSearch' | 'loading'
> {
  entity: Entity
}

function SelectAccount({ entity, label = 'CAPO account', ...rest }: Props) {
  const [text, setText] = useState('')
  const { data: accounts, isLoading } = useContactpersoonAccounts({
    entity,
    text,
  })

  return (
    <SelectAccountOptions
      {...rest}
      label={label}
      accounts={accounts}
      text={text}
      onSearch={setText}
      loading={isLoading}
    />
  )
}

function SelectAccountOptions({
  accounts,
  text,
  onSearch,
  ...rest
}: OptionsProps) {
  // MUI groepeert alleen aangrenzende opties, dus sorteren op organisatie.
  const sortedAccounts = [...accounts].sort((a, b) =>
    a.contactOrganisatie.localeCompare(b.contactOrganisatie),
  )

  return (
    <SelectObject
      {...rest}
      options={sortedAccounts}
      groupBy={({ contactOrganisatie }) => contactOrganisatie}
      onSearch={onSearch}
      getOptionLabel={({ naam, emailadres }) => `${naam} (${emailadres})`}
      isOptionEqualToValue={(option, value) => option.userid === value.userid}
      noOptionsText={
        text.trim().length < MIN_QUERY_SIZE
          ? `Type minimaal ${MIN_QUERY_SIZE} karakters`
          : 'Geen accounts gevonden'
      }
    />
  )
}
